// Direct-DOM form error helpers. Used instead of `setState({error})` so that
// the form is not re-rendered on validation failure — every input keeps its
// value, focus, scroll position, and selection. The optional `field` argument
// also marks the offending input so the user can see which one to fix.

// `host` may be either the form element itself or any ancestor that contains
// both the form and a `[data-form-error]` slot. The slot is the preferred
// place to render the banner; we fall back to inserting it just above the
// first form when the slot is missing.
function resolveSlot(host) {
  if (!host) return null;
  const slot = host.matches?.('[data-form-error]')
    ? host
    : host.querySelector?.('[data-form-error]');
  if (slot) return slot;
  // Lazy-create a slot above the first form so legacy templates still work.
  const form = host.matches?.('form') ? host : host.querySelector?.('form');
  if (!form || !form.parentNode) return null;
  const created = document.createElement('div');
  created.setAttribute('data-form-error', '');
  form.parentNode.insertBefore(created, form);
  return created;
}

function findField(host, name) {
  if (!host || !name) return null;
  const input = host.querySelector?.(`[name="${cssEscape(name)}"]`);
  if (!input) return null;
  return input.closest('.field') || input;
}

// Minimal CSS.escape polyfill — enough for the field names we use.
function cssEscape(s) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(s);
  return String(s).replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}

// Show an error message anchored to the form. If `field` is provided, the
// matching input's `.field` wrapper gets `.has-error` and we focus it for the
// user. Returns the field element (or null) so callers can act on it.
export function showFormError(host, message, opts = {}) {
  if (!host) return null;
  clearFieldErrors(host);
  const slot = resolveSlot(host);
  if (slot) {
    slot.innerHTML = '';
    const banner = document.createElement('div');
    banner.className = 'error-banner';
    banner.setAttribute('role', 'alert');
    banner.setAttribute('aria-live', 'polite');
    banner.textContent = String(message || '');
    slot.appendChild(banner);
  }
  const field = opts.field ? findField(host, opts.field) : null;
  if (field) {
    field.classList.add('has-error');
    const input = field.querySelector?.('input, select, textarea');
    if (input && typeof input.focus === 'function') {
      try { input.focus({ preventScroll: false }); } catch (_) { input.focus(); }
    }
  }
  return field;
}

export function clearFormError(host) {
  if (!host) return;
  const slot = host.matches?.('[data-form-error]')
    ? host
    : host.querySelector?.('[data-form-error]');
  if (slot) slot.innerHTML = '';
  clearFieldErrors(host);
}

function clearFieldErrors(host) {
  host.querySelectorAll?.('.field.has-error').forEach((el) => el.classList.remove('has-error'));
}

// Heuristic: pull a field name out of an ApiError thrown by the backend.
// Backend convention (where present) is `details.field` on the error envelope.
// As a fallback we scan the message for known column names.
export function fieldFromApiError(err, knownFields = []) {
  if (!err) return null;
  const detailsField = err.data?.error?.details?.field;
  if (typeof detailsField === 'string' && detailsField !== '') return detailsField;
  const msg = String(err.message || '').toLowerCase();
  for (const name of knownFields) {
    const needle = String(name).toLowerCase().replace(/_/g, ' ');
    if (msg.includes(name.toLowerCase()) || msg.includes(needle)) return name;
  }
  return null;
}
